import { useEffect, useState, useCallback } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { FaPlus, FaPen, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import { fetchAccountingPermissions, ACCOUNTING_CONFIG_EXPENSE_CAT } from "../../libs/customerCreatePermission";
import "./accountingResponsive.css";

const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const isOk = (res) => res?.data?.success !== false;
const cardStyle = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" };

const INITIAL_FORM = {
  code: "",
  name: "",
  type: "",
  parentId: "",
};

const flattenTree = (nodes, depth = 0, out = []) => {
  (nodes || []).forEach((n) => {
    out.push({ ...n, depth });
    flattenTree(n.children || [], depth + 1, out);
  });
  return out;
};

const ExpenseCategoryList = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [perm, setPerm] = useState({ canCreate: false, canEdit: false, canDelete: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchAccountingPermissions(axiosInstance, ACCOUNTING_CONFIG_EXPENSE_CAT);
      if (!cancelled) {
        setPerm({
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/accounting/expense-categories/management-tree");
      const tree = unwrap(res);
      setRows(flattenTree(Array.isArray(tree) ? tree : []));
    } catch (err) {
      toast.error("Không thể tải danh mục chi phí");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  const onFormChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const openCreate = () => {
    setEditId(null);
    setForm(INITIAL_FORM);
    setShowModal(true);
  };

  const openEdit = (row) => {
    if (row.isSystem) return;
    setEditId(row.id);
    setForm({
      code: row.code || "",
      name: row.name || "",
      type: row.type || "",
      parentId: row.parentId || "",
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name) {
      toast.warn("Vui lòng nhập mã và tên danh mục");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        parentId: form.parentId ? Number(form.parentId) : null,
      };
      const res = editId
        ? await axiosInstance.put(`/api/accounting/expense-categories/${editId}`, payload)
        : await axiosInstance.post("/api/accounting/expense-categories", payload);
      if (!isOk(res)) {
        toast.error(res?.data?.message || "Lưu danh mục thất bại");
        return;
      }
      toast.success(editId ? "Cập nhật danh mục thành công" : "Thêm danh mục thành công");
      setShowModal(false);
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.message || "Lưu danh mục thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (row) => {
    if (row.isSystem) return;
    try {
      const res = await axiosInstance.patch(`/api/accounting/expense-categories/${row.id}/active`, {
        active: !row.active,
      });
      if (!isOk(res)) {
        toast.error(res?.data?.message || "Cập nhật trạng thái thất bại");
        return;
      }
      toast.success(row.active ? "Đã tắt danh mục" : "Đã bật danh mục");
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.message || "Cập nhật trạng thái thất bại");
    }
  };

  const handleDelete = async (row) => {
    if (row.isSystem) return;
    if (row.voucherCount > 0) {
      toast.error("Không thể xóa danh mục đã có phiếu chi");
      return;
    }
    const result = await Swal.fire({
      title: "Xóa danh mục?",
      text: `Bạn có chắc muốn xóa "${row.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Xóa",
      cancelButtonText: "Hủy",
      confirmButtonColor: "#dc3545",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.delete(`/api/accounting/expense-categories/${row.id}`);
      if (!isOk(res)) {
        toast.error(res?.data?.message || "Xoá danh mục thất bại");
        return;
      }
      toast.success("Đã xoá danh mục");
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.message || "Xoá danh mục thất bại");
    }
  };

  const body = (
    <div className={embedded ? "" : "acc-page"}>
        <Card style={cardStyle}>
          <Card.Body>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <h5 className="mb-0 fw-bold">Danh mục chi phí</h5>
              {perm.canCreate && (
                <Button variant="primary" size="sm" onClick={openCreate}>
                  <FaPlus className="me-1" /> Thêm danh mục
                </Button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <div className="acc-table-wrap">
              <Table striped hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Mã</th>
                    <th>Tên danh mục</th>
                    <th className="acc-col-hide-sm">Loại</th>
                    <th>Nguồn</th>
                    <th>Trạng thái</th>
                    <th className="text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className={!row.active ? "table-secondary" : undefined}>
                      <td>{row.code}</td>
                      <td>
                        <span style={{ paddingLeft: `${row.depth * 18}px` }}>
                          {row.depth > 0 ? "└ " : ""}
                          {row.name}
                        </span>
                      </td>
                      <td className="acc-col-hide-sm">{row.type || "--"}</td>
                      <td>
                        {row.isSystem ? (
                          <Badge bg="secondary">Hệ thống</Badge>
                        ) : (
                          <Badge bg="info">Thủ công</Badge>
                        )}
                      </td>
                      <td>
                        <Form.Check
                          type="switch"
                          id={`exp-cat-active-${row.id}`}
                          checked={!!row.active}
                          disabled={!!row.isSystem || !perm.canEdit}
                          onChange={() => handleToggle(row)}
                          label={row.active ? "Bật" : "Tắt"}
                        />
                      </td>
                      <td className="text-center">
                        {!row.isSystem && (perm.canEdit || perm.canDelete) && (
                          <div className="d-flex gap-1 justify-content-center">
                            {perm.canEdit && (
                              <Button variant="outline-primary" size="sm" onClick={() => openEdit(row)}>
                                <FaPen />
                              </Button>
                            )}
                            {perm.canDelete && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={row.voucherCount > 0}
                                title={row.voucherCount > 0 ? "Đã có phiếu chi" : "Xóa"}
                                onClick={() => handleDelete(row)}
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editId ? "Sửa danh mục" : "Thêm danh mục"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSave}>
            <Modal.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mã danh mục</Form.Label>
                    <Form.Control value={form.code} onChange={(e) => onFormChange("code", e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Loại</Form.Label>
                    <Form.Control value={form.type} onChange={(e) => onFormChange("type", e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Tên danh mục</Form.Label>
                    <Form.Control value={form.name} onChange={(e) => onFormChange("name", e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Danh mục cha</Form.Label>
                    <Form.Select value={form.parentId || ""} onChange={(e) => onFormChange("parentId", e.target.value)}>
                      <option value="">-- Không có --</option>
                      {rows
                        .filter((r) => !editId || r.id !== editId)
                        .map((r) => (
                          <option key={r.id} value={r.id}>
                            {`${" ".repeat(r.depth * 2)}${r.name}`}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Đóng
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? <Spinner animation="border" size="sm" /> : "Lưu"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
    </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default ExpenseCategoryList;
